import * as THREE from 'three';
import { RatKingBoss, type RatKingVisual, type RatKingTickResult } from '../ai/ratking';
import { Enemy, type EnemyVisual, type EnemyTickResult } from '../ai/enemy';
import { ARCHETYPES } from '../ai/archetypes';
import { DenArena, DEN_RESPAWN_DELAY_RATKING, DEN_RESPAWN_DELAY_SEWER } from './arena';
import { LAIR_PILLARS, LAIR_RAT_HOLES, RATKING_HOME } from './denLair';
import { ELITE_HOME } from './denSewer';
import type { BlockPhysics } from './collision';
import type { LoadedGLB } from '../core/assets';
import { instantiate } from '../core/assets';
import type { AimTarget } from '../combat/autoaim';

/**
 * Wave D-4: orchestration for the two dens that host a live combat encounter
 * inside a real interior room (Rat King's lair, the sewer elite's chamber) —
 * Gridlock's own wreck-dressed arena stays wired the existing way (`Boss`/
 * `BossArena` in main.ts, per that class's own header comment on why it's
 * deliberately NOT rebuilt here). Both classes below share one shape: own a
 * `DenArena` (world/arena.ts's generalized dormant/active/cleared state
 * machine), the live combat entity, and — for the lair only — the swarm-call
 * add pool and pillar-crack tracking. `main.ts` drives each with `tick()`
 * every frame and calls `onEnter()`/`onExit()` at the room's own walk-
 * through door transition.
 */

const SWARM_RAT_LIFETIME_SAFETY = 30; // seconds — a safety ceiling on a swarm add that somehow never dies/despawns (leashes off and gets stuck, an asset hiccup); never expected to trip in practice, matching this project's own "belt and suspenders" convention (ai/ratking.ts's SWARM_WAIT_MAX)

export class LairEncounter {
  boss: RatKingBoss | null = null;
  readonly arena = new DenArena(DEN_RESPAWN_DELAY_RATKING);
  /** Once true (first ever clear), never resets — separate from `arena.state`, which cycles dormant/active/cleared every fight. Read/written by main.ts against `save.progress`; this class only exposes the moment a kill happens via the tick result. */
  private readonly swarmRats: Array<{ enemy: Enemy; age: number }> = [];
  private readonly pillarCracked: boolean[];
  private readonly pillarGroups: Array<{ intact: THREE.Object3D | null; cracked: THREE.Object3D | null }> = [];
  private ratGlb: LoadedGLB | null = null;
  private prevBossState: string = 'idle';
  /** Edge-detected once per kill — main.ts reads and clears this to grant the first-clear bonus exactly once per actual kill (not per tick while `state==='dead'`). */
  justDefeated = false;

  constructor() {
    this.pillarCracked = LAIR_PILLARS.map(() => false);
  }

  setRatGlb(glb: LoadedGLB): void {
    this.ratGlb = glb;
  }

  spawnBoss(visual: RatKingVisual): RatKingBoss {
    this.boss = new RatKingBoss(visual, RATKING_HOME.x, RATKING_HOME.z);
    return this.boss;
  }

  /** Called once per pillar the moment its own GLB instance is placed (`Block.attachDenLair()`) — finds the two named mesh variants the C07 kit ships (`pillar_intact`/`pillar_cracked` on ONE file, per its own NOTES.md) and starts every pillar in its intact state. */
  registerPillarGroup(index: number, group: THREE.Object3D): void {
    const intact = group.getObjectByName('pillar_intact') ?? null;
    const cracked = group.getObjectByName('pillar_cracked') ?? null;
    this.pillarGroups[index] = { intact, cracked };
    if (cracked) cracked.visible = false;
    if (intact) intact.visible = true;
  }

  onEnter(): void {
    if (this.arena.state === 'dormant') {
      this.arena.activate();
      this.boss?.engage();
    }
  }

  /** Player walked back out mid-fight — un-fight, matching Gridlock's own retreat-resets-the-encounter rule (world/arena.ts's `BossArena.reset()` comment), applied here to "left the room" instead of "left a radius." */
  onExit(): void {
    if (this.arena.state === 'active') {
      this.arena.reset();
      this.boss?.resetEncounter();
      for (const p of this.pillarCracked.keys()) this.resetPillar(p);
      this.clearSwarmRats();
    }
  }

  /**
   * Fix session (review finding, corroborated by 2 lenses as MEDIUM): neither
   * `onExit()` nor `tickRespawnClock()`'s resurrect branch ever touched
   * `swarmRats` — a mid-fight retreat (or a real kill + resurrect cycle) left
   * any still-alive adds frozen in place forever (their own `tick()` loop
   * above only runs while `insideLair`), and the NEXT fight would start with
   * those stale rats still alive on top of whatever the new SWARM CALL
   * spawns. Hides + detaches each one from the scene graph (they were parented
   * to `this.boss.group.parent`, i.e. the scene itself — `getSwarmRats()`'s
   * own gate below is the OTHER half of this fix, since this method alone
   * doesn't run every frame).
   */
  private clearSwarmRats(): void {
    for (const s of this.swarmRats) {
      s.enemy.group.visible = false;
      s.enemy.group.parent?.remove(s.enemy.group);
    }
    this.swarmRats.length = 0;
  }

  /**
   * D-4's respawn rule ("left AND timer elapsed"), the SAME contract as
   * Gridlock's own `bossArena.tickClearedTimer(dt)` wiring (main.ts's
   * comment on that call): ticks the post-clear timer every frame
   * UNCONDITIONALLY (real elapsed time, not paused while the player is
   * elsewhere on the map — `DenArena.tickClearedTimer` is already a no-op
   * outside `'cleared'`), and only resurrects once the player is ALSO
   * outside the room at the moment this runs. Bug this closes: `onEnter()`
   * only ever handles the `'dormant'` state and `onExit()` only ever
   * handles `'active'` — neither one has a `'cleared'` branch, so without
   * this method the arena had NO path back to `'dormant'` after a real
   * kill at all (the exact S10/Gridlock-precedent bug this file's own
   * header comment describes as already fixed for Gridlock — it was not,
   * in fact, ported to these two newer classes). Call every frame,
   * regardless of `insideLair` — mirrors main.ts's own Gridlock call site.
   */
  tickRespawnClock(dt: number, insideLair: boolean): void {
    this.arena.tickClearedTimer(dt);
    if (this.arena.state === 'cleared' && !insideLair && this.arena.clearedTimerDone) {
      this.arena.reset();
      this.boss?.resetEncounter();
      for (const p of this.pillarCracked.keys()) this.resetPillar(p);
      this.clearSwarmRats();
    }
  }

  private resetPillar(index: number): void {
    this.pillarCracked[index] = false;
    const g = this.pillarGroups[index];
    if (!g) return;
    if (g.intact) g.intact.visible = true;
    if (g.cracked) g.cracked.visible = false;
  }

  /** K-2: returns whether a pillar was actually cracked — `tick()` uses this to call `RatKingBoss.reportChargeBroken()` synchronously, the same tick, before the boss's own 'slam' state exits (a strictly later tick) reads that flag. */
  private crackNearestPillar(bossX: number, bossZ: number): boolean {
    let bestIdx = -1;
    let bestDist = Infinity;
    for (let i = 0; i < LAIR_PILLARS.length; i++) {
      if (this.pillarCracked[i]) continue;
      const p = LAIR_PILLARS[i];
      const d = Math.hypot(p.x - bossX, p.z - bossZ);
      if (d < bestDist) {
        bestDist = d;
        bestIdx = i;
      }
    }
    // 1.4 ≈ RATKING_RADIUS (1.0) + pillar half-width (0.4) + a small margin
    // — the charge only stopped ON a pillar if it's genuinely this close,
    // not merely somewhere in the room when the safety timeout tripped.
    if (bestIdx >= 0 && bestDist <= 1.6) {
      this.pillarCracked[bestIdx] = true;
      const g = this.pillarGroups[bestIdx];
      if (g) {
        if (g.intact) g.intact.visible = false;
        if (g.cracked) g.cracked.visible = true;
      }
      return true;
    }
    return false;
  }

  /**
   * Fix session: gated on `arena.state === 'active'` (mirrors
   * `SewerDenEncounter.aimTarget`'s own active-only gate) — the swarm pool
   * used to be returned unconditionally, so `main.ts`'s `denExtraEnemies()`
   * fed it into every aim/melee/super/projectile resolution pass even while
   * the den was dormant/cleared, 480m from the player on the street. Harmless
   * today only because `AIM_RANGE`/the weapon range gates reject a target
   * that far away — a real latent hole, not a designed guarantee.
   */
  getSwarmRats(): Enemy[] {
    return this.arena.state === 'active' ? this.swarmRats.map((s) => s.enemy) : [];
  }

  /**
   * Drives the boss FSM, the swarm-call add pool, and RED CHARGE's pillar-
   * damage hook. `physics`/`playerPos` mirror `Boss.update()`'s own contract
   * exactly (ai/boss.ts). Returns the boss's own tick result (meleeFired/
   * swarmCallFired/chargeHitPlayer) for main.ts to resolve against the
   * player via `combat/ratkingResolve.ts`, matching Gridlock's identical
   * split (main.ts owns damage resolution, the boss class owns the FSM).
   */
  tick(dt: number, playerPos: THREE.Vector3, physics: BlockPhysics): RatKingTickResult | null {
    this.justDefeated = false;
    if (!this.boss) return null;

    // Swarm rats: driven the same way EnemyManager drives a pooled Enemy —
    // separation is irrelevant at this tiny scale (max 3 alive), so a zero
    // vector is passed rather than building a real steering pass for 1-3
    // enemies.
    const zeroSep = new THREE.Vector3();
    for (let i = this.swarmRats.length - 1; i >= 0; i--) {
      const s = this.swarmRats[i];
      s.age += dt;
      const result = s.enemy.update(dt, physics, playerPos, zeroSep, null, true);
      if (result.removed || s.age > SWARM_RAT_LIFETIME_SAFETY) {
        s.enemy.group.visible = false;
        this.swarmRats.splice(i, 1);
      }
    }

    const wasCharging = this.prevBossState === 'charging';
    const result = this.boss.update(dt, playerPos, this.arena.state === 'active', physics, this.swarmRats.length);

    // RED CHARGE pillar hook: the charge always resolves into 'slam' the
    // instant it stops (ai/ratking.ts's own `updateCharging` comment — hit a
    // wall/pillar, arrived, or timed out, all funnel through the same
    // transition). This class can't see WHICH PILLAR stopped it, so it
    // infers that part via proximity — but K-2 review fix: proximity ALONE
    // can't tell "the charge actually hit something" from "the charge
    // happened to arrive or time out near a pillar it never touched" (an
    // arrival is captured at the player's frozen position, which can sit
    // anywhere, including inside a pillar's own proximity halo, WITHOUT the
    // charge ever having been physically blocked) — confirmed real by
    // review, a player who gets hit by the charge (chargeHitPlayer=true,
    // the "wrong response") near a pillar could previously still be
    // credited as if they'd used it for cover. `result.chargeStoppedByWall`
    // (ai/ratking.ts) is the real "was this a genuine physics block"
    // signal; ONLY when that's true does proximity get to decide WHICH
    // pillar gets credit.
    if (wasCharging && this.boss.state === 'slam' && result.chargeStoppedByWall) {
      if (this.crackNearestPillar(this.boss.position.x, this.boss.position.z)) this.boss.reportChargeBroken();
    }
    this.prevBossState = this.boss.state;

    if (result.swarmCallFired) {
      this.spawnSwarm();
    }

    if (this.arena.state === 'active' && this.boss.state === 'dead') {
      this.arena.clear();
      this.justDefeated = true;
    }

    return result;
  }

  private spawnSwarm(): void {
    if (!this.ratGlb) return;
    const archetype = ARCHETYPES.rat;
    for (const hole of LAIR_RAT_HOLES) {
      const inst = instantiate(this.ratGlb, archetype.tint);
      inst.group.scale.setScalar(archetype.scale);
      const visual: EnemyVisual = { group: inst.group, materials: inst.materials, mixer: inst.mixer, clips: inst.clips, baseScale: archetype.scale };
      const enemy = new Enemy(archetype, visual, hole.x, hole.z, -1, 0);
      this.boss?.group.parent?.add(inst.group);
      this.swarmRats.push({ enemy, age: 0 });
    }
  }
}

export class SewerDenEncounter {
  elite: Enemy | null = null;
  readonly arena = new DenArena(DEN_RESPAWN_DELAY_SEWER);
  justDefeated = false;
  private wasDead = false;

  spawn(visual: EnemyVisual): Enemy {
    const archetype = ARCHETYPES.sewer_elite;
    this.elite = new Enemy(archetype, visual, ELITE_HOME.x, ELITE_HOME.z, -1, 0);
    return this.elite;
  }

  onEnter(): void {
    if (this.arena.state === 'dormant') this.arena.activate();
  }

  onExit(): void {
    if (this.arena.state === 'active') this.arena.reset();
  }

  /** Same fix, same reasoning as `LairEncounter.tickRespawnClock` — see that method's own comment. Revives via `Enemy.respawn()` (S03's own pooled-reuse contract: full hp/state/position reset at its home point), not `resetEncounter()` (that's the Boss-class name). */
  tickRespawnClock(dt: number, insideSewer: boolean): void {
    this.arena.tickClearedTimer(dt);
    if (this.arena.state === 'cleared' && !insideSewer && this.arena.clearedTimerDone) {
      this.arena.reset();
      this.elite?.respawn(ELITE_HOME.x, ELITE_HOME.z, 0);
    }
  }

  /** `sewer_elite` is `movementMode:'burrower'` (ai/enemy.ts's `updateBurrower`) — no nav/separation input needed, matching that mode's own contract (its `update()` call never consults them). Returns the Enemy's own tick result so main.ts can react to a melee-style contact hit the same way it reacts to any other roster enemy's `meleeAttackFired` (the emerge burst IS a contact hit, per ai/archetypes.ts's own `sewer_elite.contactDamage`). */
  tick(dt: number, playerPos: THREE.Vector3, physics: BlockPhysics): EnemyTickResult | null {
    this.justDefeated = false;
    if (!this.elite) return null;
    if (this.arena.state !== 'active') {
      // Fix session (review finding, MEDIUM): this branch used to return
      // WITHOUT calling `elite.update()` at all — the comment claimed the
      // pose was "ticked via a zero-effect update," which the code never
      // actually did. `Enemy.update()`'s dead-state branch (`ai/enemy.ts`)
      // is the ONLY thing that advances the death fade/hides the corpse
      // (via `result.removed`), so `arena.clear()` firing the very frame
      // after the killing blow froze the elite mid-fade at ~98% scale,
      // fully visible, until the 180s respawn eventually called
      // `Enemy.respawn()`. Still call `update()` while `state==='dead'` — its
      // dead branch is a pure fade+return, it never runs aggro/attack logic
      // regardless of arena state, so this is safe even while dormant.
      if (this.elite.state === 'dead') {
        const fadeResult = this.elite.update(dt, physics, playerPos, new THREE.Vector3(), null, true);
        if (fadeResult.removed) this.elite.group.visible = false;
      }
      this.wasDead = this.elite.state === 'dead';
      return null;
    }
    const zeroSep = new THREE.Vector3();
    const result = this.elite.update(dt, physics, playerPos, zeroSep, null, true);
    const isDead = this.elite.state === 'dead';
    if (isDead && !this.wasDead) {
      this.arena.clear();
      this.justDefeated = true;
    }
    this.wasDead = isDead;
    return result;
  }

  get aimTarget(): AimTarget | null {
    return this.elite && this.arena.state === 'active' ? this.elite.aimTarget : null;
  }
}
